<!--
  COTI-502: elemento base Offset (impresion y procesos planos).

  Comparte estructura con el de Cajas, que es el otro producto plano: mismo suaje/imposicion
  y mismos procesos. Lo que cambia es la configuracion, que viene del backend por
  ElementoBaseCampos y ElementoBaseEtiquetas del elemento base 11 — sin engranaje, cilindro
  ni magneticos, y con el desarrollo etiquetado como "Largo de pliego".
-->
<template>
  <el-collapse v-model="collapseModel">
    <elemento-header
      :title="title"
      :name="name"
    >
      <input-suaje
        v-model="localModel"
        v-bind="componentsProps.InputSuaje"
        :validate="validarEspecificacionTecnicaCampos"
        :mostrarEngranaje="false"
        :mostrarAnchoLargoItem="true"
        :mostrarPliegosPorHoja="true"
        :anchoImposicion="materialRef?.Ancho"
        :largoImposicion="materialRef?.Largo"
        @update:unidadImposicion="onUnidadImposicionChange"
      />

      <input-procesos
        v-model="localModel.Secciones"
        :elemento-tipo="localModel.ElementoTipo"
      />
      <el-divider></el-divider>
    </elemento-header>
  </el-collapse>
</template>

<script setup>
import {
  onBeforeMount,
  provide,
  ref,
  computed,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useElemento } from '@/shared/composables/useElemento.js';
import { useTechnicalSpecificationStore } from '@/views/technical_specifications/stores/technical_specification_store.js';
import { useUnidadImposicionProvider } from '@/shared/composables/use_unidad_imposicion.js';
import InputProcesos from '@/shared/components/procesos/input_procesos.vue';
import InputSuaje from '@/shared/components/suaje/input_suaje.vue';
import { useComponentsProps } from '@/shared/composables/use_components_props.js';
import { useMaterialValidaAncho } from '@/shared/composables/use_material_valida_ancho.js';
import { useMinimosPliego } from '@/shared/composables/use_minimos_pliego.js';
import { useAnchoMinimoMM } from '@/shared/composables/use_ancho_minimo.js';
import { Modules } from '@/enum/modules';
import { ElementoBase } from '@/enum/elemento_base';

const emit = defineEmits(['update:modelValue']);

const technicalSpecificationStore = useTechnicalSpecificationStore();
const { validarEspecificacionTecnicaCampos } = storeToRefs(technicalSpecificationStore);
const { componentsProps } = useComponentsProps(ElementoBase.OFFSET, Modules.ESPECIFICACION_TECNICA);

const props = defineProps({
  modelValue: { type: Object, required: true },
  title: { type: String, required: true },
  name: { type: String, required: true },
});

const { localModel, collapseModel, updateModelo } = useElemento(props, emit);

// COTI-502: los mínimos que necesita la imposición, igual que en Cotización. Se recalculan y
// se reescriben en el modelo cada vez que hace falta, incluso cuando el elemento se recarga
// y llega sin ellos.
useMinimosPliego(localModel, updateModelo);

const { anchoMinimoMM } = useAnchoMinimoMM(localModel);
const selectedMaterialAnchoMM = ref(0);
// COTI-502: las dimensiones del material alimentan la imposición y precargan
// Ancho/Largo item, igual que en Cajas.
const { materialRef } = useMaterialValidaAncho(localModel);

// Proveer valores de imposición para componentes de proceso
const imposicionProvidedValue = computed(() => ({
  EtiquetasEje: localModel.value.EtiquetasEje || 0,
  EtiquetasDesarrollo: localModel.value.EtiquetasDesarrollo || 0,
}));

// COTI-633: tamaño del pliego (en mm, el modelo lo guarda siempre en mm sin importar la
// unidad de display) para que cada proceso valide su máquina contra este mismo valor.
const margenesPliegoProvidedValue = computed(() => ({
  AnchoItem: localModel.value.AnchoItem || 0,
  LargoItem: localModel.value.LargoItem || 0,
}));

provide('currentModule', Modules.ESPECIFICACION_TECNICA);
provide('anchoMinimoMM', anchoMinimoMM);
provide('currentElementoBase', ElementoBase.OFFSET);
provide('selectedMaterialAnchoMM', selectedMaterialAnchoMM);
provide('imposicion', imposicionProvidedValue);
provide('margenesPliego', margenesPliegoProvidedValue);
const { onUnidadImposicionChange } = useUnidadImposicionProvider(localModel);

onBeforeMount(() => {
  updateModelo({
    EtiquetasEje: 1,
    EtiquetasDesarrollo: 1,
  });
});
</script>

<style scoped>
.detail-container-printed {
  padding: 0;
}
.custom-label {
  font-weight: 700;
  color: #111;
}

.colors-button {
  width: 100%;
}
</style>
